// CertificatePortal.jsx
// Student flow: Identify (Email or Registration No) → [Contact add] → Form → Success

import { useState } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

const COURSE_DURATIONS = {
  "Data Science": "6-7",
  "Data Analytics With AI": "7",
  "Data Analytics": "5",
  "Business Analyst": "5",
  "Artificial Intelligence": "8-9",
  "Data Science With AI": "9",
  "Playwright Automation With AI": "3",
  "Python Playwright Automation With AI": "3",
  "API + Automation With AI": "3",
  "Java Full Stack": "6-7",
  "Python Full Stack": "6-7",
  "Mern Full Stack": "5",
  "Software Testing": "5",
  "API + Automation": "3",
  "Web Automation": "2.5",
  "AWS": "3",
  "DevOps": "3",
  "AWS+DevOps": "6",
  "Digital Marketing": "4",
  "Adv Digital Marketing": "6",
  "Ethical Hacking": "2.5",
  "Cyber Security": "5",
  "UI/UX": "3",
  "Graphics Design": "3",
  "Video Editing": "4",
  "Java with DSA": "2",
  "C with DSA": "2",
  "C & C++": "2",
};

const STEPS = {
  IDENTIFY:       "identify",
  CONTACT:        "contact",     // ✅ regNo wale student ka email + phone
  FORM:           "form",
  SUCCESS:        "success",
  LIMIT_EXCEEDED: "limit_exceeded",
};

const MODES = { EMAIL: "email", REG: "registrationNo" };

export default function CertificatePortal() {
  const [step, setStep]             = useState(STEPS.IDENTIFY);
  const [mode, setMode]             = useState(MODES.EMAIL);
  const [identifier, setIdentifier] = useState("");
  const [student, setStudent]       = useState(null);
  const [hint, setHint]             = useState("");

  const [contact, setContact] = useState({ email: "", phone: "" });
  const [form, setForm]       = useState({ course: "", months: "", completionDate: "", remark: "" });

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  /* ── Step 1: Email ya Registration No verify ── */
  async function handleVerify(e) {
    e.preventDefault();
    setError(""); setHint(""); setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/verify-student`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: identifier.trim() }),
      });
      const data = await res.json();

      if (!data.success) {
        if (data.code === "LIMIT_EXCEEDED") { setStep(STEPS.LIMIT_EXCEEDED); return; }

        // ✅ Email nahi mila → apne aap Registration No. mode
        if (data.code === "EMAIL_NOT_FOUND") {
          setMode(MODES.REG);
          setIdentifier("");
          setHint(data.message);
          return;
        }
        setError(data.message);
        return;
      }

      const s = data.student;
      setStudent(s);
      setContact({ email: s.email || "", phone: s.phone || "" });

      const firstCourse = (s.course || "").trim();
      const autoMonths  = COURSE_DURATIONS[firstCourse] || "";
      setForm((p) => ({ ...p, course: firstCourse, months: autoMonths }));

      // ✅ RegNo se mila par email nahi hai → contact step
      setStep(data.needsContact ? STEPS.CONTACT : STEPS.FORM);
    } catch {
      setError("Unable to connect to the server. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  /* ── Step 1.5: Email + Phone save ── */
  async function handleSaveContact(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/update-contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          registrationNo: student.registrationNo,
          email: contact.email.trim(),
          phone: contact.phone.trim(),
        }),
      });
      const data = await res.json();
      if (!data.success) { setError(data.message); return; }

      setStudent(data.student);
      const c = (data.student.course || "").trim();
      setForm((p) => ({ ...p, course: c, months: p.months || COURSE_DURATIONS[c] || "" }));
      setStep(STEPS.FORM);
    } catch {
      setError("Could not save your details. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  /* ── Step 2: Request submit ── */
  async function handleSubmitRequest(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/request-certificate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email:          student.email,
          phone:          student.phone || contact.phone,
          name:           student.name,
          registrationNo: student.registrationNo || "",
          course:         (form.course || "").trim() || student.course || "Mentioned in remark",
          months:         form.months,
          completionDate: form.completionDate,
          remark:         form.remark || "",
        }),
      });
      const data = await res.json();
      if (!data.success) setError(data.message);
      else setStep(STEPS.SUCCESS);
    } catch {
      setError("Failed to submit the request. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  function resetToStart() {
    setStep(STEPS.IDENTIFY);
    setMode(MODES.EMAIL);
    setIdentifier("");
    setStudent(null);
    setError(""); setHint("");
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.logo}>🎓 Uncodemy</div>
        <p style={styles.tagline}>Certificate Download Portal</p>
      </div>

      <div style={styles.card}>
        <StepIndicator current={step} />

        {/* ════ STEP 1 — Email / Registration No ════ */}
        {step === STEPS.IDENTIFY && (
          <div style={styles.section}>
            <h2 style={styles.title}>
              {mode === MODES.EMAIL ? "Enter Your Email Address" : "Enter Your Registration No."}
            </h2>
            <p style={styles.subtitle}>
              {mode === MODES.EMAIL
                ? "Kindly use the email address you used at the time of registration."
                : "You'll find your Registration No. on your admission receipt or ID card."}
            </p>

            {/* ✅ Toggle — Email ya Registration No */}
            <div style={styles.toggleRow}>
              <button type="button"
                style={{ ...styles.toggleBtn, ...(mode === MODES.EMAIL ? styles.toggleBtnActive : {}) }}
                onClick={() => { setMode(MODES.EMAIL); setIdentifier(""); setError(""); setHint(""); }}>
                ✉️ Email
              </button>
              <button type="button"
                style={{ ...styles.toggleBtn, ...(mode === MODES.REG ? styles.toggleBtnActive : {}) }}
                onClick={() => { setMode(MODES.REG); setIdentifier(""); setError(""); setHint(""); }}>
                🆔 Registration No.
              </button>
            </div>

            {hint && <div style={styles.hintBox}>ℹ️ {hint}</div>}

            <form onSubmit={handleVerify}>
              <input
                style={styles.input}
                type={mode === MODES.EMAIL ? "email" : "text"}
                placeholder={mode === MODES.EMAIL ? "student@example.com" : "UC2024/BA-Poo/01-02"}
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required disabled={loading} autoFocus
              />
              {error && <ErrorBox message={error} />}
              <button style={styles.btnPrimary} type="submit" disabled={loading}>
                {loading ? <Spinner /> : "Verify →"}
              </button>
            </form>

            <p style={styles.smallNote}>
              Can't find your record by email? Switch to <b>Registration No.</b> above.
            </p>
          </div>
        )}

        {/* ════ STEP 1.5 — Contact details ════ */}
        {step === STEPS.CONTACT && student && (
          <div style={styles.section}>
            <h2 style={styles.title}>Add Your Contact Details</h2>
            <p style={styles.subtitle}>
              Record found ✅ — please confirm your email address and phone number so we can send your certificate.
            </p>

            <div style={styles.infoCard}>
              <InfoRow label="Name" value={student.name} />
              <InfoRow label="Registration No." value={student.registrationNo} />
              <InfoRow label="Course" value={student.course} />
            </div>

            <form onSubmit={handleSaveContact} style={{ marginTop: 18 }}>
              <label style={styles.label}>Email Address *</label>
              <input style={styles.input} type="email" placeholder="student@example.com"
                value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                required disabled={loading} autoFocus />

              <label style={styles.label}>Phone Number *</label>
              <input style={styles.input} type="tel" placeholder="9876543210"
                value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                required disabled={loading} />

              <p style={styles.note}>
                📧 Your certificate will be sent to this email address — please double-check it.
              </p>

              {error && <ErrorBox message={error} />}

              <div style={styles.row}>
                <button type="button" style={styles.btnSecondary} onClick={resetToStart} disabled={loading}>
                  ← Back
                </button>
                <button style={styles.btnPrimary} type="submit" disabled={loading}>
                  {loading ? <Spinner /> : "Save & Continue →"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ════ STEP 2 — Details ════ */}
        {step === STEPS.FORM && student && (
          <div style={styles.section}>
            <h2 style={styles.title}>Confirm Your Details</h2>

            <div style={styles.infoCard}>
              <InfoRow label="Name" value={student.name} />
              <InfoRow label="Email" value={student.email} />
              {student.phone ? <InfoRow label="Phone" value={student.phone} /> : null}
              {student.registrationNo ? <InfoRow label="Registration No." value={student.registrationNo} /> : null}
            </div>

            <p style={{ ...styles.subtitle, marginTop: 20 }}>Kindly provide the remaining details.</p>

            <form onSubmit={handleSubmitRequest}>
              {/* ⭐ Course — ek se zyada course ho to yahan se chuno / edit karo */}
              <label style={styles.label}>Course *</label>
              {(student.courses || []).length > 1 && (
                <select
                  style={{ ...styles.input, marginBottom: 8 }}
                  value={(student.courses || []).includes(form.course) ? form.course : ""}
                  onChange={(e) => {
                    const c = e.target.value;
                    setForm({ ...form, course: c, months: COURSE_DURATIONS[c] || form.months });
                  }}
                  disabled={loading}
                >
                  <option value="">-- Select your course --</option>
                  {(student.courses || []).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              )}
              <input
                style={styles.input}
                placeholder="e.g. Software Testing"
                value={form.course}
                onChange={(e) => {
                  const c = e.target.value;
                  setForm({ ...form, course: c, months: COURSE_DURATIONS[c.trim()] || form.months });
                }}
                required disabled={loading}
              />
              <p style={{ ...styles.smallNote, textAlign: "left", marginTop: -10, marginBottom: 14 }}>
                Doing more than one course? Request them one at a time — each gets its own certificate.
              </p>

              <label style={styles.label}>Course Duration (months)</label>
              <select style={styles.input} value={form.months}
                onChange={(e) => setForm({ ...form, months: e.target.value })}
                required disabled={loading}>
                <option value="">Select months</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>{m} {m === 1 ? "Month" : "Months"}</option>
                ))}
              </select>

              <label style={styles.label}>Completion Date</label>
              <input style={styles.input} type="date" value={form.completionDate}
                onChange={(e) => setForm({ ...form, completionDate: e.target.value })}
                required disabled={loading} />

              <label style={styles.label}>Remark (optional)</label>
              <textarea style={{ ...styles.input, minHeight: 70, resize: "vertical" }}
                placeholder="Any updates? Add them in the remarks below"
                value={form.remark}
                onChange={(e) => setForm({ ...form, remark: e.target.value })}
                disabled={loading} />

              {error && <ErrorBox message={error} />}

              <div style={styles.row}>
                <button type="button" style={styles.btnSecondary} onClick={resetToStart} disabled={loading}>
                  ← Back
                </button>
                <button style={styles.btnPrimary} type="submit" disabled={loading}>
                  {loading ? <Spinner /> : "Request Certificate →"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ════ STEP 3 — Success ════ */}
        {step === STEPS.SUCCESS && (
          <div style={{ ...styles.section, textAlign: "center" }}>
            <div style={styles.successIcon}>✅</div>
            <h2 style={styles.title}>Request Submitted Successfully!</h2>
            <p style={styles.subtitle}>
              Your certificate request has been sent to the admin for review.
              <br />
              Once it is approved, your <b>certificate download link</b> will be emailed to you.
            </p>
            <div style={styles.infoCard}>
              <InfoRow label="Name" value={student?.name} />
              <InfoRow label="Email" value={student?.email} />
              <InfoRow label="Course" value={student?.course} />
            </div>
            <p style={styles.note}>
              ⏳ Requests are usually processed within 3–4 business days.
              <br />
              🔐 The download link in that email stays valid for <b>2 days</b>.
              <br />
              Please keep an eye on your inbox, and check the Spam/Junk folder too.
            </p>
          </div>
        )}

        {/* ════ LIMIT EXCEEDED ════ */}
        {step === STEPS.LIMIT_EXCEEDED && (
          <div style={{ ...styles.section, textAlign: "center" }}>
            <div style={styles.successIcon}>🚫</div>
            <h2 style={{ ...styles.title, color: "#dc2626" }}>Download Limit Exceeded</h2>
            <p style={styles.subtitle}>
              Your certificate has already been downloaded 5 times.
              <br />
              Please contact the administrator to download it again.
            </p>
            <p style={styles.note}>
              📧 Admin: <a href="mailto:admin@uncodemy.com" style={{ color: "#ff5421" }}>admin@uncodemy.com</a>
            </p>
          </div>
        )}
      </div>

      <p style={styles.footer}>© Uncodemy Edutech Pvt. Ltd.</p>
    </div>
  );
}

/* ── Sub-components ── */

function StepIndicator({ current }) {
  const steps = [
    { key: STEPS.IDENTIFY, label: "Verify" },
    { key: STEPS.CONTACT,  label: "Contact" },
    { key: STEPS.FORM,     label: "Details" },
    { key: STEPS.SUCCESS,  label: "Done" },
  ];
  const activeIdx = steps.findIndex((s) => s.key === current);

  return (
    <div style={styles.stepRow}>
      {steps.map((s, i) => (
        <div key={s.key} style={styles.stepItem}>
          <div style={{
            ...styles.stepDot,
            background: i <= activeIdx ? "#ff5421" : "#e2e8f0",
            color:      i <= activeIdx ? "#fff" : "#94a3b8",
          }}>
            {i < activeIdx ? "✓" : i + 1}
          </div>
          <span style={{
            ...styles.stepLabel,
            color: i <= activeIdx ? "#ff5421" : "#94a3b8",
            fontWeight: i === activeIdx ? 600 : 400,
          }}>{s.label}</span>
          {i < steps.length - 1 && (
            <div style={{ ...styles.stepLine, background: i < activeIdx ? "#ff5421" : "#e2e8f0" }} />
          )}
        </div>
      ))}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={styles.infoRow}>
      <span style={styles.infoLabel}>{label}</span>
      <span style={styles.infoValue}>{value || "—"}</span>
    </div>
  );
}

function ErrorBox({ message }) { return <div style={styles.errorBox}>{message}</div>; }
function Spinner() { return <span style={styles.spinner}>⏳</span>; }

/* ── Styles ── */

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)",
    display: "flex", flexDirection: "column", alignItems: "center",
    padding: "40px 16px", fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  header: { textAlign: "center", marginBottom: 32 },
  logo: { fontSize: 28, fontWeight: 700, color: "#ff5421", letterSpacing: "-0.5px" },
  tagline: { color: "#64748b", fontSize: 14, marginTop: 4 },
  card: {
    background: "#fff", borderRadius: 16,
    boxShadow: "0 4px 24px rgba(79,70,229,0.10)",
    padding: "36px 40px", width: "100%", maxWidth: 480,
  },
  section: { display: "flex", flexDirection: "column" },
  title: { fontSize: 22, fontWeight: 700, color: "#1e1b4b", margin: "0 0 8px" },
  subtitle: { color: "#64748b", fontSize: 14, margin: "0 0 20px", lineHeight: 1.6 },
  label: { fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6, display: "block" },
  input: {
    width: "100%", padding: "12px 14px", borderRadius: 10,
    border: "1.5px solid #e2e8f0", fontSize: 15, color: "#1a1a1a",
    marginBottom: 16, boxSizing: "border-box", outline: "none",
  },
  toggleRow: { display: "flex", gap: 8, marginBottom: 16 },
  toggleBtn: {
    flex: 1, padding: "10px", borderRadius: 10,
    border: "1.5px solid #e2e8f0", background: "#fff",
    color: "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer",
  },
  toggleBtnActive: { background: "#fff1ec", color: "#ff5421", border: "1.5px solid #ff5421" },
  hintBox: {
    background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8",
    borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 14, lineHeight: 1.5,
  },
  smallNote: { fontSize: 12, color: "#94a3b8", marginTop: 14, textAlign: "center" },
  btnPrimary: {
    width: "100%", padding: "13px", background: "#ff5421", color: "#fff",
    border: "none", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer", marginTop: 4,
  },
  btnSecondary: {
    padding: "12px 20px", background: "#f1f5f9", color: "#475569",
    border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer",
  },
  row: { display: "flex", gap: 12, marginTop: 4 },
  errorBox: {
    background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626",
    borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 14,
  },
  infoCard: { background: "#f8fafc", borderRadius: 10, padding: "16px 18px", border: "1px solid #e2e8f0" },
  infoRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "6px 0", borderBottom: "1px solid #f1f5f9", gap: 12,
  },
  infoLabel: { fontSize: 13, color: "#64748b", fontWeight: 500 },
  infoValue: { fontSize: 14, color: "#1e1b4b", fontWeight: 600, textAlign: "right", wordBreak: "break-all" },
  stepRow: { display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 28 },
  stepItem: { display: "flex", alignItems: "center", gap: 6 },
  stepDot: {
    width: 28, height: 28, borderRadius: "50%", display: "flex",
    alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0,
  },
  stepLabel: { fontSize: 11 },
  stepLine: { width: 24, height: 2, marginLeft: 6, marginRight: 6, borderRadius: 2 },
  successIcon: { fontSize: 48, marginBottom: 12 },
  note: {
    background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8,
    padding: "10px 14px", fontSize: 13, color: "#92400e", marginTop: 16, lineHeight: 1.6,
  },
  footer: { color: "#94a3b8", fontSize: 12, marginTop: 24 },
  spinner: { display: "inline-block" },
};